import React from "react";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { VariablePair } from "./CreateRulePrompt";
import { Code, Type } from "@/shared/schema";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Label } from "../ui/label";

interface MapVariableProps {
    variablePairs: VariablePair[];
    updateVariablePair: (index: number, updates: Partial<VariablePair>) => void;
    currentPairIndex: number;
    setCurrentPairIndex: (currentPairIndex: number) => void
    dataTypeOptions: Type[];
    attributeOptions: Code[];
    setAttrCode: (code: string) => void;
    onBack: () => void;
    onNext: () => void;
    generatedRule: string | null;
}

const operandOptions = [
    { value: '==', label: 'Equals (==)' },
    { value: '!=', label: 'Not Equals (!=)' },
    { value: '>', label: 'Greater Than (>)' },
    { value: '<', label: 'Less Than (<)' },
    { value: '>=', label: 'Greater or Equal (>=)' },
    { value: '<=', label: 'Less or Equal (<=)' }
];

const logicalOperatorOptions = [
    { value: 'AND', label: 'AND' },
    { value: 'OR', label: 'OR' }
];

export const MapVariablesStep = ({
    variablePairs,
    updateVariablePair,
    currentPairIndex,
    setCurrentPairIndex,
    dataTypeOptions,
    attributeOptions,
    setAttrCode,
    onBack,
    onNext,
    generatedRule
}: MapVariableProps) => {
    const currentPair = variablePairs[currentPairIndex];

    const handleDataTypeChange = (value: Type) => {
        updateVariablePair(currentPairIndex, {
            mappedDataType: value.Name,
            mappedAttribute: null
        });
        setAttrCode(String(value.ID));
    };

    const handleAttributeChange = (value: Code) => {
        updateVariablePair(currentPairIndex, {
            mappedAttribute: value.Name
        });
    };

    const handleOperandChange = (value: string) => {
        updateVariablePair(currentPairIndex, {
            mappedOperand: value
        });
    };

    const handleLogicalOperatorChange = (value: string) => {
        updateVariablePair(currentPairIndex, {
            mappedLogicalOperator: value
        });
    };

    const tokenizeRule = () => {
        if (!generatedRule) return [];

        let text = generatedRule;
        const tokens: any[] = [];

        const allOccurrences: Array<{
            position: number;
            pair: VariablePair;
            index: number;
            placeholder: string;
        }> = [];

        variablePairs.forEach((pair: any, index) => {
            const placeholder = pair.placeholder;
            let searchPos = 0;
            let foundPos = text.indexOf(placeholder, searchPos);

            while (foundPos !== -1) {
                const isOccupied = allOccurrences.some(occ =>
                    (foundPos >= occ.position && foundPos < occ.position + occ.placeholder.length) ||
                    (foundPos + placeholder.length > occ.position && foundPos < occ.position + occ.placeholder.length)
                );

                if (!isOccupied) {
                    allOccurrences.push({
                        position: foundPos,
                        pair,
                        index,
                        placeholder
                    });
                    break;
                }

                searchPos = foundPos + 1;
                foundPos = text.indexOf(placeholder, searchPos);
            }
        });

        allOccurrences.sort((a, b) => a.position - b.position);

        let lastIndex = 0;

        allOccurrences.forEach(({ position, pair, index }) => {
            if (position > lastIndex) {
                tokens.push({
                    type: "text",
                    value: text.substring(lastIndex, position)
                });
            }

            let isMapped = false;
            let displayText = pair.placeholder;

            if (pair.type === 'datatype-attribute') {
                isMapped = !!(pair.mappedDataType && pair.mappedAttribute);
                displayText = isMapped ? `${pair.mappedDataType}.${pair.mappedAttribute}` : pair.placeholder;
            } else if (pair.type === 'operand') {
                isMapped = !!pair.mappedOperand;
                displayText = isMapped ? pair?.mappedOperand ?? "" : pair.placeholder;
            } else if (pair.type === 'logical-operator') {
                isMapped = !!pair.mappedLogicalOperator;
                displayText = isMapped ? pair.mappedLogicalOperator ?? "" : pair.placeholder;
            }

            const isCurrent = index === currentPairIndex;

            tokens.push({
                type: "placeholder",
                placeholder: pair.placeholder,
                pairIndex: index,
                isMapped,
                isCurrent,
                displayText,
                tokenType: pair.type
            });

            lastIndex = position + pair.placeholder.length;
        });

        if (lastIndex < text.length) {
            tokens.push({
                type: "text",
                value: text.substring(lastIndex)
            });
        }

        return tokens;
    };

    const getColorClass = (tokenType: string, isMapped: boolean, isCurrent: boolean) => {
        if (isCurrent) {
            return "bg-blue-200 border-2 border-blue-500";
        }
        if (tokenType === 'datatype-attribute') {
            return isMapped ? "bg-green-200 border border-green-400 hover:bg-green-300" : "bg-yellow-200 border border-yellow-400 hover:bg-yellow-300";
        } else if (tokenType === 'operand') {
            return isMapped ? "bg-purple-200 border border-purple-400 hover:bg-purple-300" : "bg-orange-200 border border-orange-400 hover:bg-orange-300";
        } else if (tokenType === 'logical-operator') {
            return isMapped ? "bg-teal-200 border border-teal-400 hover:bg-teal-300" : "bg-pink-200 border border-pink-400 hover:bg-pink-300";
        }
        return "";
    };

    const completedPairsCount = variablePairs.filter(pair => {
        if (pair.type === 'datatype-attribute') {
            return pair.mappedDataType && pair.mappedAttribute;
        } else if (pair.type === 'operand') {
            return pair.mappedOperand;
        } else if (pair.type === 'logical-operator') {
            return pair.mappedLogicalOperator;
        }
        return false;
    }).length;

    const isAllPairsCompleted = variablePairs.every(pair => {
        if (pair.type === 'datatype-attribute') {
            return pair.mappedDataType && pair.mappedAttribute;
        } else if (pair.type === 'operand') {
            return pair.mappedOperand;
        } else if (pair.type === 'logical-operator') {
            return pair.mappedLogicalOperator;
        }
        return false;
    });

    const isCurrentPairComplete = () => {
        if (!currentPair) return false;
        if (currentPair.type === 'datatype-attribute') {
            return currentPair.mappedDataType && currentPair.mappedAttribute;
        } else if (currentPair.type === 'operand') {
            return currentPair.mappedOperand;
        } else if (currentPair.type === 'logical-operator') {
            return currentPair.mappedLogicalOperator;
        }
        return false;
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Badge variant="outline">
                        Mapping {currentPairIndex + 1} of {variablePairs.length}
                    </Badge>
                    <Badge variant="secondary">
                        {completedPairsCount} of {variablePairs.length} completed
                    </Badge>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Rule Preview</CardTitle>
                    {/* <CardDescription className="text-xs space-y-1">
                        <div>🟡 Yellow: DataType.Attribute (unmapped) → 🟢 Green: Mapped</div>
                        <div>🟠 Orange: Operand (unmapped) → 🟣 Purple: Mapped</div>
                        <div>🩷 Pink: Logical Operator (unmapped) → 🩵 Teal: Mapped</div>
                    </CardDescription> */}
                </CardHeader>
                <CardContent>
                    <ScrollArea className="h-[200px] w-full rounded-md border bg-slate-50 p-4">
                        <code className="text-sm font-mono whitespace-pre-wrap">
                            {tokenizeRule().map((token: any, idx: number) => {
                                if (token.type === "text") {
                                    return <span key={idx}>{token.value}</span>;
                                }

                                const { pairIndex, isMapped, isCurrent, displayText, tokenType } = token;
                                const colorClass = getColorClass(tokenType, isMapped, isCurrent);

                                return (
                                    <span
                                        key={idx}
                                        className={`${colorClass} px-1 py-0.5 rounded font-bold cursor-pointer transition-colors`}
                                        onClick={() => setCurrentPairIndex(pairIndex || 0)}
                                        title={`Click to map this ${tokenType.replace('-', ' ')}`}
                                    >
                                        {displayText}
                                    </span>
                                );
                            })}
                        </code>
                    </ScrollArea>
                </CardContent>
            </Card>

            <Card className="border-2 border-blue-200">
                <CardHeader>
                    <CardTitle className="text-lg">
                        {currentPair?.type === 'datatype-attribute' && `Map DataType.Attribute ${currentPairIndex + 1}`}
                        {currentPair?.type === 'operand' && `Map Operand ${currentPairIndex + 1}`}
                        {currentPair?.type === 'logical-operator' && `Map Logical Operator ${currentPairIndex + 1}`}
                    </CardTitle>
                    <CardDescription>
                        {currentPair?.type === 'datatype-attribute' && 'Select the data type and attribute for the highlighted variable'}
                        {currentPair?.type === 'operand' && 'Select the comparison operator'}
                        {currentPair?.type === 'logical-operator' && 'Select the logical operator (AND/OR)'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {currentPair?.type === 'datatype-attribute' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Data Type</Label>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" className="w-full text-left text-sm">
                                            {dataTypeOptions.length > 0 ? !currentPair?.mappedDataType ? "Select" : currentPair?.mappedDataType : "Fetching DataType..."}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        {dataTypeOptions.map((opt) => (
                                            <DropdownMenuItem
                                                className="w-[250px]"
                                                key={opt.Name}
                                                onClick={() => handleDataTypeChange(opt)}
                                            >
                                                {opt.Name}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>

                            <div className="space-y-2">
                                <Label>Attribute</Label>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" className="w-full text-left text-sm" disabled={!currentPair?.mappedDataType || attributeOptions.length === 0}>
                                            {attributeOptions.length > 0 ? !currentPair?.mappedAttribute ? "Select" : currentPair?.mappedAttribute : "Fetching Attributes..."}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        {attributeOptions.map((opt) => (
                                            <DropdownMenuItem
                                                className="w-[250px]"
                                                key={opt.Name}
                                                onClick={() => handleAttributeChange(opt)}
                                            >
                                                {opt.Name}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                    )}

                    {currentPair?.type === 'operand' && (
                        <div className="space-y-2">
                            <Label>Comparison Operator</Label>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" className="w-full text-left text-sm">
                                        {currentPair?.mappedOperand || "Select Operator"}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                    {operandOptions.map((opt) => (
                                        <DropdownMenuItem
                                            className="w-[250px]"
                                            key={opt.value}
                                            onClick={() => handleOperandChange(opt.value)}
                                        >
                                            {opt.label}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}

                    {currentPair?.type === 'logical-operator' && (
                        <div className="space-y-2">
                            <Label>Logical Operator</Label>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" className="w-full text-left text-sm">
                                        {currentPair?.mappedLogicalOperator || "Select Logical Operator"}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                    {logicalOperatorOptions.map((opt) => (
                                        <DropdownMenuItem
                                            className="w-[250px]"
                                            key={opt.value}
                                            onClick={() => handleLogicalOperatorChange(opt.value)}
                                        >
                                            {opt.label}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}

                    {isCurrentPairComplete() && (
                        <Alert>
                            <Check className="h-4 w-4" />
                            <AlertTitle>Current Mapping Complete</AlertTitle>
                            <AlertDescription>
                                <code className="font-mono text-sm">
                                    {currentPair.type === 'datatype-attribute' && `${currentPair.mappedDataType}.${currentPair.mappedAttribute}`}
                                    {currentPair.type === 'operand' && currentPair.mappedOperand}
                                    {currentPair.type === 'logical-operator' && currentPair.mappedLogicalOperator}
                                </code>
                            </AlertDescription>
                        </Alert>
                    )}
                </CardContent>
            </Card>

            <div className="flex justify-between gap-4">
                <Button
                    variant="outline"
                    onClick={onBack}
                    className="gap-2"
                >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                </Button>

                <Button
                    onClick={onNext}
                    disabled={!isAllPairsCompleted}
                    className="gap-2"
                >
                    Next <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
};